import type {
  DamZoneJob,
  DamZoneListResponse,
  DamZoneRequestError,
  DamZoneRequestResponse,
  DamZoneSummary,
} from "@chro/shared";
import type { ZoneMapTone, ZoneMapZone } from "../../../components/eex/research/ZoneMap";

// Presentation rules for EEX Research → Data. The server decides every state;
// these helpers only name it.
type ZoneState = DamZoneSummary["state"];
type Validation = NonNullable<DamZoneSummary["validation"]>;

export const IN_PROGRESS_STATES: ReadonlySet<ZoneState> = new Set(["queued", "downloading", "validating", "paused"]);
export const STORED_STATES: ReadonlySet<ZoneState> = new Set(["ready", "ready_with_gaps", "stored_partial"]);
export const RESEARCH_POLL_MS = 5_000;

export function isInProgress(zone: DamZoneSummary): boolean {
  return IN_PROGRESS_STATES.has(zone.state);
}

export function zoneTone(zone: DamZoneSummary): ZoneMapTone {
  if (zone.availability === "unavailable") return "unavailable";
  if (zone.state === "ready" || zone.state === "ready_with_gaps") return "stored";
  if (zone.state === "stored_partial") return "partial";
  if (zone.state === "failed") return "failed";
  return isInProgress(zone) ? "progress" : "available";
}

const STATE_LABELS: Record<ZoneState, string> = {
  not_stored: "Not stored",
  queued: "Queued",
  downloading: "Downloading",
  validating: "Validating",
  paused: "Paused",
  stored_partial: "Partly stored",
  ready: "Stored",
  ready_with_gaps: "Stored, source gaps",
  failed: "Failed",
};

export function zoneStatusLabel(zone: DamZoneSummary): string {
  return zone.availability === "unavailable" ? "Not on ENTSO-E" : STATE_LABELS[zone.state];
}

export function zoneMapZones(zones: readonly DamZoneSummary[]): ZoneMapZone[] {
  return zones.flatMap((zone) => zone.mapShapeId
    ? [{ shapeId: zone.mapShapeId, title: `${zone.name} · ${zoneStatusLabel(zone)}`, tone: zoneTone(zone) }]
    : []);
}

export type ZoneGroupId = "stored" | "progress" | "available" | "unavailable";
export const ZONE_GROUPS: readonly { id: ZoneGroupId; label: string }[] = [
  { id: "progress", label: "In progress" },
  { id: "stored", label: "Stored" },
  { id: "available", label: "Available to add" },
  { id: "unavailable", label: "Not on ENTSO-E" },
];

export function zoneGroup(zone: DamZoneSummary): ZoneGroupId {
  if (zone.availability === "unavailable") return "unavailable";
  if (isInProgress(zone)) return "progress";
  // A failed download or check keeps whatever it already stored.
  return STORED_STATES.has(zone.state) || zone.coverage !== null ? "stored" : "available";
}

export function groupZones(zones: readonly DamZoneSummary[]): Record<ZoneGroupId, DamZoneSummary[]> {
  const groups: Record<ZoneGroupId, DamZoneSummary[]> = { stored: [], progress: [], available: [], unavailable: [] };
  for (const zone of zones) groups[zoneGroup(zone)].push(zone);
  for (const list of Object.values(groups)) list.sort((a, b) => a.name.localeCompare(b.name));
  return groups;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ISO dates are calendar days; format them without a time zone round trip.
export function formatDay(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  return `${day} ${MONTHS[month! - 1]} ${year}`;
}

export function formatMonth(iso: string): string {
  const [year, month] = iso.split("-").map(Number);
  return `${MONTHS[month! - 1]} ${year}`;
}

export function formatRange(from: string, to: string): string {
  return from === to ? formatDay(from) : `${formatDay(from)} – ${formatDay(to)}`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return "under a minute";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `about ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `about ${hours} h` : `about ${hours} h ${rest} min`;
}

function clock(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

const PAUSE_TEXT: Record<NonNullable<DamZoneJob["pauseReason"]>, string> = {
  rate_limited: "ENTSO-E rate limit",
  source_retry: "ENTSO-E unavailable, retrying",
  restarting: "resuming after a restart",
};

const ERROR_TEXT: Record<NonNullable<DamZoneJob["errorCode"]>, string> = {
  source_unavailable: "ENTSO-E was unavailable.",
  rate_limited: "ENTSO-E kept limiting our requests.",
  token_invalid: "ENTSO-E rejected the API token.",
  no_source_data: "ENTSO-E has no prices for this zone.",
  pipeline_error: "Processing the prices failed.",
};

export function jobKindLabel(job: DamZoneJob): string {
  return job.kind === "gap_retry" ? "Retrying missing days" : job.kind === "revalidate" ? "Re-checking stored days" : "Full history";
}

// One line describing where a job is, for the progress bar caption.
export function jobProgressText(job: DamZoneJob): string {
  switch (job.phase) {
    case "queued":
      return job.queuePosition ? `Queued, ${ordinal(job.queuePosition)} in line` : "Queued";
    case "downloading":
      return `Downloading ${job.currentMonth ? `${formatMonth(job.currentMonth)} · ` : ""}${job.monthsDone} of ${job.monthsTotal} months`;
    case "validating":
      return `Validating · ${job.validationDone} of ${job.validationTotal} checks`;
    case "paused":
      return `Paused, ${PAUSE_TEXT[job.pauseReason ?? "restarting"]}${job.resumesAt ? ` · resumes ${clock(job.resumesAt)}` : ""}`;
    case "failed":
      return `Stopped after ${job.monthsDone} of ${job.monthsTotal} months. ${job.errorCode ? ERROR_TEXT[job.errorCode] : "The download failed."}`;
    default:
      return "Finished";
  }
}

// Queued, downloading, validating or paused; finished jobs show no progress bar.
export function isJobActive(job: DamZoneJob): boolean {
  return job.phase === "queued" || job.phase === "downloading" || job.phase === "validating" || job.phase === "paused";
}

export function jobEtaText(job: DamZoneJob): string | null {
  return job.etaSeconds === null ? null : `${formatDuration(job.etaSeconds)} left`;
}

function ordinal(value: number): string {
  const tens = value % 100;
  const suffix = tens >= 11 && tens <= 13 ? "th" : ["th", "st", "nd", "rd"][value % 10] ?? "th";
  return `${value}${suffix}`;
}

export interface ZoneRequestAction {
  label: string;
  explanation: string;
}

// The button a permitted user sees; null when there is nothing to request.
export function zoneRequestAction(zone: DamZoneSummary): ZoneRequestAction | null {
  if (zone.availability === "unavailable" || isInProgress(zone) || zone.state === "ready") return null;
  const shared = "Every company can use the prices once stored.";
  if (zone.state === "not_stored") {
    return { label: `Add ${zone.name}`, explanation: `Downloads the full ${zone.name} day-ahead price history into the shared CASCENA database. ${shared}` };
  }
  if (zone.state === "ready_with_gaps") {
    return { label: "Retry missing days", explanation: "Asks ENTSO-E again for the days it did not publish. You can retry once a day." };
  }
  if (zone.state === "failed") {
    return zone.coverage?.fullHistory
      ? { label: "Download again", explanation: "Downloads the full history again and re-runs the checks. Days already stored are kept." }
      : { label: "Retry download", explanation: `Continues the download where it stopped. ${shared}` };
  }
  return { label: "Download full history", explanation: `Fills in the rest of the history. ${shared}` };
}

export function requestBlockedText(reason: DamZoneListResponse["requestBlockedReason"], role?: string): string | null {
  switch (reason) {
    case "not_permitted":
      return role === "trader" ? "An admin has turned off adding zones for your account." : "Only EEX admins and traders can add zones.";
    case "downloads_disabled": return "Adding zones is switched off for now.";
    case "no_entsoe_token": return "Adding zones needs an ENTSO-E API token. A company admin can add one under Admin → ENTSO-E.";
    default: return null;
  }
}

export function requestOutcomeText(response: DamZoneRequestResponse): string {
  const name = response.zone.name;
  switch (response.outcome) {
    case "queued": return `${name} is queued. The download runs in the background, so you can leave this page.`;
    case "coalesced": return `${name} is already downloading. Your request joined it.`;
    case "retry_queued": return `Retrying ${name}.`;
    case "gap_retry_queued": return `Asking ENTSO-E again for the missing ${name} days.`;
    case "already_available": return `${name} is already fully stored.`;
  }
}

export function requestErrorText(error: DamZoneRequestError): string {
  if (error.code === "retry_cooldown" && error.retryAfterSeconds) {
    return `${error.error}. Try again in ${formatDuration(error.retryAfterSeconds)}.`;
  }
  return error.error.endsWith(".") ? error.error : `${error.error}.`;
}

const GAP_REASONS: Record<Validation["gapRanges"][number]["reason"], string> = {
  source_missing: "not published by ENTSO-E",
  source_partial: "partly published",
  source_currency: "not published in EUR",
  source_anomaly: "failed plausibility checks",
};

export function gapReasonText(reason: keyof typeof GAP_REASONS): string {
  return GAP_REASONS[reason];
}

export function validationText(validation: Validation): string {
  switch (validation.state) {
    case "passed": return "Checked: no missing days.";
    case "passed_with_gaps": return "Checked: every missing day is missing at ENTSO-E.";
    case "failed": return "Checked: some days could not be stored.";
    default: return "Not checked yet.";
  }
}

export function futuresCheckText(validation: Validation): string {
  const check = validation.futuresCheck;
  if (check) {
    return `${check.monthsMatched} of ${check.monthsCompared} months match the ${check.family} final settlement within ±${check.toleranceEurMwh} €/MWh (from ${formatMonth(check.firstMonth)}).`;
  }
  return validation.futuresCheckUnavailableReason === "no_eex_reference"
    ? "No EEX future settles on this zone's price."
    : "No EEX final settlements are stored yet to compare against.";
}

export function flagTexts(validation: Validation): string[] {
  const counts = validation.flagCounts;
  const days = (count: number) => `${count} ${count === 1 ? "day" : "days"}`;
  return [
    counts.outOfBounds > 0 ? `${days(counts.outOfBounds)} with prices outside the auction limits` : null,
    counts.a03Filled > 0 ? `${days(counts.a03Filled)} with repeated prices expanded from ENTSO-E's compact format` : null,
    counts.unexpectedResolution > 0 ? `${days(counts.unexpectedResolution)} at an unexpected resolution` : null,
    counts.duplicateSeriesResolved > 0 ? `${days(counts.duplicateSeriesResolved)} where duplicate series were resolved` : null,
    counts.currencyConverted > 0 ? `${days(counts.currencyConverted)} converted to EUR from the local currency` : null,
  ].filter((text): text is string => text !== null);
}

export function resolutionText(coverage: NonNullable<DamZoneSummary["coverage"]>): string | null {
  const segments = coverage.resolutionSegments;
  if (segments.length === 0) return null;
  if (segments.length === 1) return `${segments[0]!.minutes}-minute prices`;
  return segments.map((segment, index) => index === segments.length - 1
    ? `${segment.minutes}-minute from ${formatDay(segment.from)}`
    : `${segment.minutes}-minute until ${formatDay(segment.to)}`).join(", ");
}

export function mapSummary(zones: readonly DamZoneSummary[]): string {
  const groups = groupZones(zones);
  return `Map of European bidding zones: ${groups.stored.length} stored, ${groups.progress.length} in progress, ${groups.available.length} available to add.`;
}
